import React from "react";
import {
  Box,
  Heading,
  Input,
  HStack,
  Stack,
  RadioGroup,
  Radio,
  Divider,
  Text,
  Tooltip,
} from "@chakra-ui/react";

const SectionBox = ({ title, children }) => (
  <Box
    mt={6}
    p={4}
    border="2px solid"
    borderColor="gray.600"
    borderRadius="md"
    bg="white"
    boxShadow="md"
    textAlign="left"
  >
    <Heading size="md" mb={2}>{title}</Heading>
    <Divider mb={4} />
    {children}
  </Box>
);

const FacilitySection = ({
  facilityName,
  setFacilityName,
  facilityAddress,
  setFacilityAddress,
  shippingContact,
  setShippingContact,
  selectedFacility,
  setSelectedFacility,
  originalData = {},
}) => {
  const getNestedValue = (obj, path) =>
    path.split(".").reduce((acc, part) => acc?.[part], obj);

  const getBorderColor = (name, value) => {
    const original = getNestedValue(originalData, name);
    const hasOriginal = original !== undefined && original !== null && original !== "";
    const hasValue = value !== undefined && value !== null && value !== "";

    if (!hasOriginal && hasValue) return "2px solid green"; // Added
    if (hasOriginal && !hasValue) return "2px solid red";   // Deleted
    if (hasOriginal && hasValue && original !== value) return "2px solid blue"; // Changed
    return "1px solid #E2E8F0"; // Unchanged
  };

  const getTooltipLabel = (name, value) => {
    const original = getNestedValue(originalData, name);
    const hasOriginal = original !== undefined && original !== null && original !== "";
    const hasValue = value !== undefined && value !== null && value !== "";

    if (!hasOriginal && hasValue) return "Added new value";
    if (hasOriginal && !hasValue) return `Deleted previous value: ${original}`;
    if (hasOriginal && hasValue && original !== value) return `Changed from: ${original}`;
    return "";
  };

  const renderInput = (label, name, value, onChange) => (
    <Box flex="1">
      <Text fontSize="sm" mb={1}>{label}</Text>
      <Tooltip label={getTooltipLabel(name, value)} isDisabled={!getTooltipLabel(name, value)}>
        <Input
          value={value || ""}
          onChange={onChange}
          border={getBorderColor(name, value)}
        />
      </Tooltip>
    </Box>
  );

  return (
    <SectionBox title="Facility Information">
      <HStack spacing={4} mb={3}>
        {renderInput("Facility Name", "facilityName", facilityName, (e) => setFacilityName(e.target.value))}
        {renderInput("Facility NPI", "facilityAddress.npi", facilityAddress.npi, (e) =>
          setFacilityAddress({ ...facilityAddress, npi: e.target.value })
        )}
      </HStack>

      <HStack spacing={4} mb={3}>
        {renderInput("Facility TIN", "facilityAddress.tin", facilityAddress.tin, (e) =>
          setFacilityAddress({ ...facilityAddress, tin: e.target.value })
        )}
        {renderInput("Facility PTAN", "facilityAddress.ptan", facilityAddress.ptan, (e) =>
          setFacilityAddress({ ...facilityAddress, ptan: e.target.value })
        )}
      </HStack>

      {/* NEW: phone + fax fields (fax existed previously; ensure both show + diff) */}
      <HStack spacing={4} mb={3}>
        {renderInput("Facility Phone", "facilityAddress.phone", facilityAddress.phone, (e) =>
          setFacilityAddress({ ...facilityAddress, phone: e.target.value })
        )}
        {renderInput("Facility Fax", "facilityAddress.fax", facilityAddress.fax, (e) =>
          setFacilityAddress({ ...facilityAddress, fax: e.target.value })
        )}
      </HStack>

      <Box mb={3}>
        {renderInput("Street Address", "facilityAddress.street", facilityAddress.street, (e) =>
          setFacilityAddress({ ...facilityAddress, street: e.target.value })
        )}
      </Box>

      <HStack spacing={4} mb={3}>
        {renderInput("City", "facilityAddress.city", facilityAddress.city, (e) =>
          setFacilityAddress({ ...facilityAddress, city: e.target.value })
        )}
        {renderInput("State", "facilityAddress.state", facilityAddress.state, (e) =>
          setFacilityAddress({ ...facilityAddress, state: e.target.value })
        )}
        {renderInput("Zip Code", "facilityAddress.zip", facilityAddress.zip, (e) =>
          setFacilityAddress({ ...facilityAddress, zip: e.target.value })
        )}
      </HStack>

      <Box mb={3}>
        {renderInput("Shipping Contact", "shippingContact", shippingContact, (e) => setShippingContact(e.target.value))}
      </Box>

      <Text fontSize="sm" mb={2} fontWeight="semibold">Place of Service</Text>
      <Tooltip
        label={getTooltipLabel("selectedFacility", selectedFacility)}
        isDisabled={!getTooltipLabel("selectedFacility", selectedFacility)}
      >
        <Box
          border={getBorderColor("selectedFacility", selectedFacility)}
          borderRadius="md"
          p={2}
        >
          <RadioGroup onChange={setSelectedFacility} value={selectedFacility}>
            <Stack spacing={2}>
              <Radio value="Physician Office (11)">Physician Office (11)</Radio>
              <Radio value="Home (12)">Home (12)</Radio>
              <Radio value="Hospital Outpatient (22)">Hospital Outpatient (22)</Radio>
              <Radio value="Ambulatory Surgical Center (24)">Ambulatory Surgical Center (24)</Radio>
              <Radio value="Mobile Nursing Home (32)">Mobile Nursing Home (32)</Radio>
              <Radio value="Other">Other</Radio>
            </Stack>
          </RadioGroup>
        </Box>
      </Tooltip>
    </SectionBox>
  );
};

export default FacilitySection;
